import { ChevronDown, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BuilderTooltip } from '../BuilderTooltip';
import styles from './searchable-select.module.css';
import { SearchableSelectOptionButton } from './searchable-select-option';

export interface SearchableSelectOption {
  id: string;
  label: string;
  description?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  disabled?: boolean;
  /**
   * Rendered inside the field box ahead of the label. Sits alongside the trigger rather than
   * within it, so it may be interactive (a tooltip trigger) without nesting inside a button.
   */
  leadingIcon?: ReactNode;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  isLoading = false,
  loadingText = 'Loading...',
  emptyText = 'No options found',
  disabled = false,
  leadingIcon,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(() => options.find(opt => opt.id === value), [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(opt => opt.label?.toLowerCase().includes(query));
  }, [options, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const isDisabled = disabled || isLoading;
  const triggerClass = isDisabled ? `${styles.trigger} ${styles.disabled}` : styles.trigger;
  const labelClass = selectedOption ? styles.hasValue : undefined;
  const containerClass = leadingIcon
    ? `${styles.container} ${styles.withLeadingIcon}`
    : styles.container;

  const triggerButton = (
    <button
      type="button"
      className={triggerClass}
      onClick={() => !isDisabled && setIsOpen(!isOpen)}
      disabled={isDisabled}>
      <span className={labelClass}>
        {isLoading ? loadingText : selectedOption?.label || placeholder}
      </span>
      <aside>
        {value && !isDisabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClear(e as unknown as React.MouseEvent);
              }
            }}
            className={styles.clearButton}>
            <X />
          </span>
        )}
        <ChevronDown />
      </aside>
    </button>
  );

  return (
    <div className={containerClass} ref={containerRef}>
      {leadingIcon && <span className={styles.leadingIcon}>{leadingIcon}</span>}
      {selectedOption ? (
        <BuilderTooltip label={selectedOption.label} description={selectedOption.description}>
          {triggerButton}
        </BuilderTooltip>
      ) : (
        triggerButton
      )}

      {isOpen && (
        <nav className={styles.dropdown}>
          <search>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          </search>
          {filteredOptions.length === 0 ? (
            <p className={styles.emptyState}>{emptyText}</p>
          ) : (
            <ul>
              {filteredOptions.map(option => (
                <li key={option.id} className={option.id === value ? styles.selected : undefined}>
                  <SearchableSelectOptionButton option={option} onSelect={handleSelect} />
                </li>
              ))}
            </ul>
          )}
        </nav>
      )}
    </div>
  );
};
